'use client'

import { Box, Chip, FormControlLabel, Paper, Radio, Typography } from '@mui/material'
import type { JSX } from 'react'
import { tss } from 'tss-react/mui'
import { CheckCircleIcon } from '../Icon/catalog'
import { TRatePlan } from './PlanSelector'

const useStyles = tss.withNestedSelectors<'isSelectedTitle' | 'isMostPopular'>().create(({ classes, theme }) => ({
    isSelectedTitle: {},
    isMostPopular: {},

    holdingPageCard: {
        paddingTop: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        width: '185px',

        [theme.breakpoints.down('sm')]: {
            width: '109px',
        },
    },
    planCard: {
        minHeight: '144px',
        background: '#ffffff',
        borderRadius: '6px',
        cursor: 'pointer',
        [`&.${classes.isMostPopular}`]: {
            borderRadius: '0 0 6px 6px',
        },
        [`&.${classes.isSelectedTitle}`]: {
            background: 'linear-gradient(-206.56505117707798deg, rgb(30, 112, 161) 0%, rgb(173, 230, 255) 100%)',
        },
        '&:not(:last-child)': {
            [theme.breakpoints.down('sm')]: {
                marginRight: theme.spacing(1),
            },
        },
    },
    cardText: {
        textAlign: 'center',
        color: theme.palette.primary.dark,

        [`&.${classes.isSelectedTitle}`]: {
            color: '#ffffff',
        },
    },
    cardTextSpacing: {
        marginBottom: theme.spacing(1),
    },
    radioInput: {
        justifyContent: 'flex-end',
        padding: 0,
        margin: 0,
    },
    icon: {
        position: 'sticky',
    },
    styledChip: {
        position: 'relative',
        left: theme.spacing(0.25),
        backgroundColor: theme.palette.warning.main,
        color: theme.palette.grey[100],
        alignSelf: 'center',
        marginBottom: theme.spacing(1),
    },
    hideChip: {
        position: 'relative',
        left: theme.spacing(0.25),
        backgroundColor: theme.palette.warning.main,
        color: 'transparent',
        alignSelf: 'center',
        marginBottom: theme.spacing(1),
        visibility: 'hidden',
    },
}))

const PlanCard = ({
    plan,
    isMostPopular,
    selectedPlan,
    setSelectedPlan,
}: {
    plan: TRatePlan
    isMostPopular: boolean
    selectedPlan?: string
    setSelectedPlan: (id: string) => void
}): JSX.Element => {
    const { classes, cx } = useStyles()
    const isSelected = selectedPlan === plan.id
    return (
        <Paper
            className={cx(
                classes.planCard,
                isMostPopular && classes.isMostPopular,
                isSelected && classes.isSelectedTitle
            )}
            role="button"
            onClick={() => setSelectedPlan(plan.id)}
        >
            <Box className={classes.holdingPageCard}>
                {plan?.savingPercentage ? (
                    <Chip className={classes.styledChip} label={`Save ${plan.savingPercentage}%`} size="small" />
                ) : (
                    <Chip className={classes.hideChip} size="small" />
                )}

                <Typography
                    className={cx(classes.cardText, classes.cardTextSpacing, isSelected && classes.isSelectedTitle)}
                    variant="body2"
                >
                    {plan?.promotionPageLabel}
                </Typography>
                <Typography className={cx(classes.cardText, isSelected && classes.isSelectedTitle)} variant="h4">
                    {plan?.recurrencePricePerMonthFormatted}
                </Typography>
                <Typography className={cx(classes.cardText, isSelected && classes.isSelectedTitle)} variant="caption">
                    per month
                </Typography>
                <FormControlLabel
                    className={classes.radioInput}
                    value={isSelected}
                    control={
                        <Radio
                            size="medium"
                            checked={isSelected}
                            checkedIcon={<CheckCircleIcon color="success.main" className={classes.icon} />}
                        />
                    }
                    label=""
                />
            </Box>
        </Paper>
    )
}

export default PlanCard
